import React from 'react';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  Legend,
} from 'recharts';
import type { ResponseRateTrendPoint } from '../../../../api/queries';
import { DateFormatter } from '../utils/DateFormatter';
import classes from './ResponseRateTrendChart.module.css';

export interface ResponseRateTrendChartProps {
  data: ResponseRateTrendPoint[];
  language: string;
}

interface ChartDataPoint {
  surveyId: string;
  date: string;
  shortDate: string;
  responseRate: number;
  responsesReceived: number;
  recipientCount: number;
  surveyIndex: number;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataPoint;
    value: number;
  }>;
}

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;

  return (
    <div className={classes.tooltip}>
      <p className={classes.tooltipDate}>Undersøkelse #{data.surveyIndex}</p>
      <p className={classes.tooltipDate}>{data.shortDate}</p>
      <p className={classes.tooltipValue}>
        <span className={classes.tooltipLabel}>Svarprosent:</span>{' '}
        <strong>{data.responseRate.toFixed(1)}%</strong>
      </p>
      <p className={classes.tooltipValue}>
        <span className={classes.tooltipLabel}>Mottakere:</span>{' '}
        {data.recipientCount}
      </p>
      <p className={classes.tooltipValue}>
        <span className={classes.tooltipLabel}>Svar:</span>{' '}
        {data.responsesReceived}
      </p>
    </div>
  );
};

export const ResponseRateTrendChart: React.FC<ResponseRateTrendChartProps> = ({
  data,
  language,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className={classes.container}>
        <p className={classes.noData}>Ingen trenddata tilgjengelig</p>
      </div>
    );
  }

  const chartData: ChartDataPoint[] = data.map((point, index) => ({
    surveyId: point.surveyId,
    date: point.date,
    shortDate: DateFormatter.formatShort(point.date, language),
    responseRate: point.responseRate,
    responsesReceived: point.responsesReceived,
    recipientCount: point.recipientCount,
    surveyIndex: index + 1, // 1-indexed for display
  }));

  return (
    <div className={classes.container}>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="responseRateGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--ds-color-accent-base-default)" stopOpacity={0.8} />
              <stop offset="95%" stopColor="var(--ds-color-accent-base-default)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--ds-color-neutral-border-subtle)" />
          <XAxis
            dataKey="shortDate"
            dy={8}
            height={60}
            stroke="var(--ds-color-neutral-text-subtle)"
            fontSize={12}
          />
          <YAxis
            domain={[0, 100]}
            tickCount={6}
            dx={-4}
            stroke="var(--ds-color-neutral-text-subtle)"
            fontSize={12}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend verticalAlign="top" height={36} />
          <Area
            name="Svarprosent"
            type="monotone"
            dataKey="responseRate"
            stroke="var(--ds-color-accent-base-default)"
            strokeWidth={3}
            fillOpacity={0.2}
            fill="url(#responseRateGradient)"
            dot={{
              strokeWidth: 2,
              stroke: 'var(--ds-color-accent-base-default)',
              fill: 'var(--ds-color-neutral-background-default)',
              r: 4,
            }}
            activeDot={{
              strokeWidth: 2,
              stroke: 'var(--ds-color-accent-base-default)',
              fill: 'var(--ds-color-accent-base-default)',
              r: 6,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
